import React, { useEffect, useState } from 'react';
import { odAPI, companiesAPI } from '../services/api';
import { useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Briefcase, ShieldCheck, ShieldX, Loader2 } from 'lucide-react';

const STATUS_VARIANT = {
  pending_manager: 'warning', manager_approved: 'default', approved: 'success', rejected: 'destructive', cancelled: 'secondary',
};

const ODAdmin = () => {
  const { user } = useAuth();
  const isHR = ['super_admin', 'hr_admin'].includes(user?.role);
  const isManager = user?.role === 'manager';
  const [tab, setTab] = useState(isHR ? 'all' : 'team');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState('all');
  const [action, setAction] = useState({ open: false, mode: null, req: null });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      let r;
      if (tab === 'team') r = await odAPI.team(status !== 'all' ? status : null);
      else {
        const params = {};
        if (status !== 'all') params.status = status;
        if (company !== 'all') params.company_id = company;
        r = await odAPI.listAll(params);
      }
      setRows(r.data || []);
      if (companies.length === 0) {
        try { const c = await companiesAPI.getAll(); setCompanies(c.data || []); } catch (_e) { /* ignore */ }
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [tab, status, company]);

  const open = (mode, req) => { setAction({ open: true, mode, req }); setNote(''); };
  const apply = async () => {
    if (!action.req) return;
    setBusy(true);
    try {
      if (action.mode === 'approve_manager') await odAPI.approve(action.req.id, note, 'manager');
      else if (action.mode === 'approve_hr') await odAPI.approve(action.req.id, note, 'hr');
      else if (action.mode === 'reject') await odAPI.reject(action.req.id, note);
      toast.success('Done');
      setAction({ open: false, mode: null, req: null });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Briefcase size={22} />OD Approvals</h1>
          <p className="text-muted-foreground text-sm">Approve On-Duty requests. Approved days auto-mark attendance as Present-OD.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {(isHR || isManager) && <Button variant={tab === 'team' ? 'default' : 'outline'} onClick={() => setTab('team')} data-testid="od-tab-team">My Team</Button>}
          {isHR && <Button variant={tab === 'all' ? 'default' : 'outline'} onClick={() => setTab('all')} data-testid="od-tab-all">All (HR)</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-52" data-testid="od-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending_manager">Pending manager</SelectItem>
            <SelectItem value="manager_approved">Awaiting HR</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {tab === 'all' && isHR && (
          <Select value={company} onValueChange={setCompany}>
            <SelectTrigger className="w-52" data-testid="od-filter-company"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" />Loading…</div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Nothing here.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="od-admin-list">
          {rows.map((w) => (
            <Card key={w.id} data-testid={`od-admin-row-${w.id}`}>
              <CardContent className="p-4 flex items-center gap-3 flex-wrap">
                <Briefcase size={16} className="text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{w.employee_name || w.employee_email}</span>
                    <Badge variant={STATUS_VARIANT[w.status] || 'secondary'}>{w.status.replace('_', ' ')}</Badge>
                    <Badge variant="outline" className="text-xs">{w.od_type}</Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {w.start_date} → {w.end_date} · {w.days} day(s) · {w.purpose} {w.location ? `· ${w.location}` : ''}
                  </div>
                </div>
                {w.status === 'pending_manager' && (
                  <>
                    <Button size="sm" onClick={() => open('approve_manager', w)} data-testid={`od-approve-mgr-${w.id}`}><ShieldCheck size={14} className="mr-1" />Manager Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => open('reject', w)} data-testid={`od-reject-${w.id}`}><ShieldX size={14} className="mr-1" />Reject</Button>
                  </>
                )}
                {w.status === 'manager_approved' && isHR && (
                  <>
                    <Button size="sm" onClick={() => open('approve_hr', w)} data-testid={`od-approve-hr-${w.id}`}><ShieldCheck size={14} className="mr-1" />HR Approve</Button>
                    <Button size="sm" variant="destructive" onClick={() => open('reject', w)} data-testid={`od-reject-hr-${w.id}`}><ShieldX size={14} className="mr-1" />Reject</Button>
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={action.open} onOpenChange={(o) => setAction({ ...action, open: o })}>
        <DialogContent className="max-w-md" data-testid="od-action-dialog">
          <DialogHeader>
            <DialogTitle>
              {action.mode === 'approve_manager' && 'Manager approval'}
              {action.mode === 'approve_hr' && 'HR approval'}
              {action.mode === 'reject' && 'Reject OD'}
            </DialogTitle>
            <DialogDescription>{action.req?.employee_name} · {action.req?.start_date} → {action.req?.end_date} · {action.req?.purpose}</DialogDescription>
          </DialogHeader>
          <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="od-action-note" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction({ open: false, mode: null, req: null })}>Cancel</Button>
            <Button onClick={apply} disabled={busy} data-testid="od-action-confirm">{busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null}Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ODAdmin;
